import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import InvTabBar from '../../components/InvTabBar';
import FilterToolbar from '../../components/FilterToolbar';
import { getConsumptions, confirmConsumption } from '../../api/dispensing';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import { STORE_CONSUMPTION_TABS, CONSUMPTION_TYPE_LABELS } from '../../constants/storeManagement';
import { captureChargesAfterPatientConfirm } from '../../utils/consumptionChargeCapture';
import {
  consumptionPatientId,
  formatConsumptionPatient,
  resolveConsumptionPatientNames,
} from '../../utils/consumptionPatient';
import { storeDisplayName } from '../../utils/storeEntity';
import { formatDate } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import useActiveStore from '../../hooks/useActiveStore';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

export default function ConsumptionList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { isStoreScoped, activeStoreId } = useActiveStore();
  const [activeTab, setActiveTab] = useState('all');
  const [allItems, setAllItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [patientNames, setPatientNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmRemarks, setConfirmRemarks] = useState('');
  const [confirming, setConfirming] = useState(false);

  const createBase = url.replace(/\/consumptions\/?$/, '/consumption/new');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // A store-scoped user (Store Keeper) sees only their own store.
      // API: GET /inventory/dispensing/consumption/
      const data = await getConsumptions(isStoreScoped && activeStoreId ? { store_id: activeStoreId } : undefined);
      const list = toInventoryList(data);
      setAllItems(list);
      resolveConsumptionPatientNames(list)
        .then(setPatientNames)
        .catch(() => setPatientNames({}));
    } catch (e) {
      setError(e.message);
      setAllItems([]);
      setPatientNames({});
    } finally {
      setLoading(false);
    }
  }, [isStoreScoped, activeStoreId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // API: GET /inventory/stores/
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => setStores([]));
  }, []);

  const resolveStoreName = useCallback((row) => {
    const name = row?.store_name || row?.from_store_name;
    if (name) return name;
    const storeId = row?.from_store_id || row?.store_id;
    if (!storeId) return '—';
    const store = stores.find(s => String(s.store_id || s.id) === String(storeId));
    return store ? storeDisplayName(store) : storeId;
  }, [stores]);

  const items = useMemo(() => {
    let rows = allItems;
    if (activeTab === 'ward') {
      rows = rows.filter(r => r.consumption_type === 'WARD' || r.consumption_type === 'GENERAL');
    } else if (activeTab === 'patient') {
      rows = rows.filter(r => r.consumption_type === 'PATIENT');
    }
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const id = consumptionPatientId(r);
      const name = (patientNames[id] || '').toLowerCase();
      const label = formatConsumptionPatient(r, patientNames).toLowerCase();
      return name.includes(q) || id.toLowerCase().includes(q) || label.includes(q);
    });
  }, [allItems, activeTab, search, patientNames]);

  const statusChipMinWidth = useMemo(() => {
    const labels = items
      .map(r => (r.status ? String(r.status).replace(/_/g, ' ') : ''))
      .filter(Boolean);
    if (!labels.length) return undefined;
    return Math.ceil(Math.max(...labels.map(l => l.length)) * 7 + 20);
  }, [items]);

  async function doConfirm() {
    if (!can(INV_ACTIONS.CONSUMPTION_CREATE)) return;
    setConfirming(true);
    try {
      const id = confirmTarget.transaction_id || confirmTarget.id;
      // API: POST /inventory/dispensing/consumption/{id}/confirm/
      await confirmConsumption(id, { remarks: confirmRemarks.trim() || undefined });

      if (confirmTarget.consumption_type === 'PATIENT') {
        try {
          await captureChargesAfterPatientConfirm(confirmTarget);
          toast.success('Consumption confirmed and billed');
        } catch (chargeErr) {
          toast.warning(
            chargeErr.message
              || 'Consumption confirmed, but pharmacy charge capture failed',
          );
        }
      } else {
        toast.success('Consumption confirmed — stock deducted');
      }

      setConfirmTarget(null);
      setConfirmRemarks('');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to confirm consumption');
    } finally {
      setConfirming(false);
    }
  }

  const columns = useMemo(() => [
    { field: 'transaction_number', headerName: 'Consumption #', width: 170 },
    {
      field: 'store_id',
      headerName: 'Store',
      width: 200,
      valueGetter: p => resolveStoreName(p.row),
    },
    {
      field: 'consumption_type',
      headerName: 'Type',
      width: 120,
      valueFormatter: p => CONSUMPTION_TYPE_LABELS[p.value] || p.value || '—',
    },
    {
      field: 'patient_id',
      headerName: 'Patient',
      width: 220,
      valueGetter: p => formatConsumptionPatient(p.row, patientNames),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: p => <StatusChip status={p.value} minWidth={statusChipMinWidth} />,
    },
    {
      field: 'billing_status',
      headerName: 'Billing',
      width: 120,
      valueFormatter: p => p.value || '—',
    },
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 120,
      valueFormatter: p => formatDate(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: p => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => history.push(`${url}/${p.row.transaction_id || p.row.id}`)}
              sx={{ color: IPD_COLORS.primary }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.status === 'DRAFT' && can(INV_ACTIONS.CONSUMPTION_CREATE) && (
            <Tooltip title="Confirm">
              <IconButton
                size="small"
                onClick={() => {
                  setConfirmRemarks('');
                  setConfirmTarget(p.row);
                }}
                sx={{ color: IPD_COLORS.success.text }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [url, history, can, statusChipMinWidth, patientNames, resolveStoreName]);

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management"
        title="Consumptions"
        description={`${items.length} record${items.length === 1 ? '' : 's'}${isStoreScoped ? ' · your store only' : ''}`}
        actions={can(INV_ACTIONS.CONSUMPTION_CREATE) ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <InvButton
              startIcon={<AddIcon />}
              onClick={() => history.push(`${createBase}?type=WARD`)}
            >
              Ward Consumption
            </InvButton>
            <InvButton
              startIcon={<AddIcon />}
              onClick={() => history.push(`${createBase}?type=PATIENT`)}
            >
              Patient Consumption
            </InvButton>
          </Box>
        ) : null}
      />

      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by patient name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search by patient name' }}
        />
      </FilterToolbar>

      <InvTabBar
        tabs={STORE_CONSUMPTION_TABS}
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Consumption type filters"
        sx={{ mb: 1.5 }}
      />

      <InvDataGrid
        rows={items}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        getRowId={r => r.transaction_id || r.id}
        emptyMessage={
          search.trim()
            ? 'No consumptions match this patient name.'
            : activeTab === 'ward'
              ? 'No ward consumptions found.'
              : activeTab === 'patient'
                ? 'No patient consumptions found.'
                : 'No consumptions found.'
        }
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Confirm Consumption"
        description={`Confirm "${confirmTarget?.transaction_number || 'this consumption'}"? Stock will be deducted.`}
        optionalReason
        reasonLabel="Remarks"
        reasonValue={confirmRemarks}
        onReasonChange={setConfirmRemarks}
        loading={confirming}
        onConfirm={doConfirm}
        onCancel={() => {
          setConfirmTarget(null);
          setConfirmRemarks('');
        }}
      />
    </Box>
  );
}
